import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const InteractiveBarChart = ({
  initialData = [],
  height = 300,
  interactive = true,
  colorScheme = ["#6FA4FF", "#FF8686", "#8CFFCF", "#FCFF6C", "#AA8DFF"],
  showLabels = true,
  showValues = true,
  title = "Interactive Bar Chart",
  animateChanges = true,
  onDataChange = () => {},
}) => {
  const [data, setData] = useState(initialData);
  const [editingIndex, setEditingIndex] = useState(null);
  const [maxValue, setMaxValue] = useState(0);

  // Make chart more responsive
  const chartBottomPadding = 40;
  const axisLeftPadding = 8;

  useEffect(() => {
    // Update the maximum value for scaling
    const max = Math.max(...data.map((item) => item.value), 10);
    setMaxValue(Math.ceil(max / 5) * 5); // Round up to nearest 5
  }, [data]);

  useEffect(() => {
    // Update data when initialData changes
    if (initialData && initialData.length > 0) {
      setData(initialData);
    }
  }, [initialData]);

  const handleBarDrag = (index, value) => {
    // Ensure value is within 0-maxValue
    const newValue = Math.max(0, Math.min(value, maxValue));

    // Create a new data array with the updated value
    const newData = [...data];
    newData[index] = { ...newData[index], value: Math.round(newValue) };

    setData(newData);
    onDataChange(newData);
  };

  const handleBarClick = (index) => {
    if (!interactive) return;

    if (editingIndex === index) {
      setEditingIndex(null);
    } else {
      setEditingIndex(index);
    }
  };

  const handleValueInputChange = (index, value) => {
    // Validate input is a number and within valid range
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    const newValue = Math.max(0, Math.min(numValue, maxValue));

    // Update data
    const newData = [...data];
    newData[index] = { ...newData[index], value: newValue };

    setData(newData);
    onDataChange(newData);
  };

  // Generate scale lines - simplified for better mobile display
  const generateScaleLines = () => {
    const lines = [];
    const step = maxValue / 4; // Reduced number of lines (4 instead of 5)

    for (let i = 0; i <= 4; i++) {
      const value = i * step;
      const yPosition = height - height * (value / maxValue);

      lines.push(
        <div
          key={`line-${i}`}
          className="absolute w-full border-t border-gray-300"
          style={{ top: yPosition }}
        >
          <span className="absolute -left-6 -top-2 text-xs text-gray-500">
            {Math.round(value)}
          </span>
        </div>
      );
    }

    return lines;
  };

  return (
    <div className="w-full">
      <h3 className="text-center text-base font-medium text-[#0D065B] mb-3">
        {title}
      </h3>
      <div
        className="relative w-full"
        style={{ height: `${height + chartBottomPadding}px` }}
      >
        {/* Y-axis */}
        <div className="absolute left-8 top-0 w-px h-full bg-gray-400"></div>

        {/* Scale lines */}
        <div className="absolute left-8 top-0 right-0 h-full">
          {generateScaleLines()}
        </div>

        {/* X-axis */}
        <div className="absolute left-8 right-0 bottom-40 h-px bg-gray-400"></div>

        {/* Bars */}
        <div className="absolute left-12 right-0 top-0 bottom-40 flex items-end justify-around">
          {data.map((item, index) => {
            const barHeight = (item.value / maxValue) * height;
            const barColor =
              item.color || colorScheme[index % colorScheme.length];

            return (
              <div
                key={`bar-${index}`}
                className="flex flex-col items-center"
                style={{ width: `${100 / data.length}%` }}
              >
                {/* Bar */}
                <motion.div
                  className={`w-4/5 rounded-t-md relative ${
                    interactive ? "cursor-ns-resize" : ""
                  }`}
                  style={{
                    backgroundColor: barColor,
                    height: barHeight,
                    // For charts with many bars, limit minimum width
                    minWidth: data.length > 8 ? 10 : 15,
                  }}
                  initial={{ height: 0 }}
                  animate={{
                    height: barHeight,
                    backgroundColor:
                      editingIndex === index ? `${barColor}CC` : barColor,
                  }}
                  transition={{ duration: animateChanges ? 0.5 : 0 }}
                  onClick={() => handleBarClick(index)}
                  drag={interactive ? "y" : false}
                  dragConstraints={{ top: 0, bottom: 0 }}
                  dragElastic={0}
                  onDrag={(_, info) => {
                    if (!interactive) return;

                    // Calculate the new value based on drag position
                    const dragOffset = info.offset.y;
                    const percentChange = dragOffset / height;
                    const valueChange = maxValue * percentChange;
                    const newValue = item.value - valueChange;

                    handleBarDrag(index, newValue);
                  }}
                >
                  {/* Value label */}
                  {showValues && (
                    <motion.div
                      className="absolute -top-5 left-0 right-0 text-center text-xs font-medium"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {Math.round(item.value)}
                    </motion.div>
                  )}

                  {/* Editing input (shows when bar is clicked) */}
                  {interactive && editingIndex === index && (
                    <motion.div
                      className="absolute -top-8 left-0 right-0 flex justify-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <input
                        type="number"
                        min="0"
                        max={maxValue}
                        value={item.value}
                        onChange={(e) =>
                          handleValueInputChange(index, e.target.value)
                        }
                        className="w-10 h-6 text-xs text-center border border-gray-300 rounded"
                        autoFocus
                      />
                    </motion.div>
                  )}
                </motion.div>

                {/* Category label - truncated for better fit */}
                {showLabels && (
                  <div
                    className="mt-1 text-xs text-center font-medium text-[#0D065B] max-w-full px-1 truncate"
                    style={{
                      fontSize: data.length > 8 ? "0.6rem" : "0.75rem",
                      transform: data.length > 8 ? "rotate(-45deg)" : "none",
                      transformOrigin: "center left",
                      maxWidth: "100%",
                      marginTop: data.length > 8 ? "8px" : "4px",
                    }}
                  >
                    {item.category}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InteractiveBarChart;
